import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button } from '@boozallen-uip/uswds-react-core';

/**
 * Success confirmation shown where the admin acted (inside the card, or in the slot a moved
 * card left behind). Focus moves here on mount so screen readers announce it and keyboard
 * users stay in place; the Save button that triggered it becomes disabled and would otherwise
 * drop focus to the top of the page.
 *
 * On a successful undo the parent removes this notice; it only handles the in-flight and
 * failure states itself.
 */
export default function AccountChangeNotice({ children, onUndo, undoContext, className }) {
  const containerRef = useRef(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const [hasUndoError, setHasUndoError] = useState(false);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const undoHandler = async () => {
    if (isUndoing) {
      return;
    }
    setIsUndoing(true);
    setHasUndoError(false);
    try {
      await onUndo();
    } catch {
      setHasUndoError(true);
      setIsUndoing(false);
    }
  };

  return (
    <div ref={containerRef} tabIndex={-1} role="status" className={className}>
      <Alert type="success" slim>
        <span>{children}</span>{' '}
        {/* aria-disabled rather than disabled so focus stays on the link if the undo fails. */}
        <Button unstyled type="button" onClick={undoHandler} aria-disabled={isUndoing || undefined}>
          {isUndoing ? 'Undoing...' : 'Undo'}
          <span className="usa-sr-only">{` ${undoContext}`}</span>
        </Button>
      </Alert>
      {hasUndoError ? (
        <Alert type="error" slim className="mt-2">
          Change not undone. Please try again in a few moments.
        </Alert>
      ) : null}
    </div>
  );
}

AccountChangeNotice.propTypes = {
  children: PropTypes.node.isRequired,
  onUndo: PropTypes.func.isRequired,
  undoContext: PropTypes.string.isRequired,
  className: PropTypes.string,
};
